import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FeedbackStatus } from '@kar-dmp/pattern-library';

import BidFormActive from './BidFormActive';
import Controls from './Controls';
import Header from './Header';
import History from './History';
import BidBoxContext from '../BidBoxContainer/BidBoxContext';
import DealerSelectDropdown from '../DealerSelectDropdown';
import { useOmpDealerContext } from '../OmpDealerProvider';
import Summary, { SUMMARY_VARIANT } from './Summary';

import ROUTES from '../../Routes/constants';
import Alert from '../../../_shared/components/Alert';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import bidErrors from '../../../_shared/constants/errorCodes';
import ReducerConstants from '../../../_shared/constants/reducers';
import timeouts from '../../../_shared/constants/timeouts';
import getSelectedDealerId from '../../../_shared/helpers/getSelectedDealerId';
import renderLocale from '../../../_shared/helpers/renderLocale';
import stringToLiteral from '../../../_shared/helpers/stringToLiteral';
import useMpBidTracking from '../../../_shared/hooks/useMixpanelBidTracking';
import usePlaceBid from '../../../_shared/hooks/usePlaceBid';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';
import useTradeChange from '../../../_shared/hooks/useTradeChange';
import useManufacturerList from '../../../_shared/hooks/vehicle/useManufacturerList';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import getBidModalLogic from '../../../_shared/utils/getBidModalLogic';
import getQuickBidIncrements from '../../../_shared/utils/getQuickBidIncrements';
import {
  BidConfirmationModalType,
  BidSource,
  BidStatus,
  BidType
} from '../../../_shared/types/auction';
import {
  TradeContext,
  TradeState,
  TradeViewVariant,
  BidPlaceErrorCode,
  FetchAuctionErrorCodes
} from '../../../_shared/types/trade';
import { bidSlice } from '../../../redux/slices/bid';
import { updateSelectedTradeAuctionSummary } from '../../../redux/slices/ui/selectedTrades';
import BidService from '../../../services/BidService';
import ErrorService from '../../../services/ErrorService';

import StyledBidBox, {
  StyledAddPaymentMethodLink,
  StyledBiddingDisclaimer,
  StyledBiddingSection,
  StyledContent,
  StyledKarStatusBanner,
  DealerNoPaymentMethodContainer,
  BidControlsContainer,
  StyledFadeBorder
} from './Styles';
import TRToast from '../../../_shared/helpers/TRToast';
import useAnalytics from '../../../_shared/hooks/useAnalytics';
import mixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import bidPlaceErrorCodeToMessageId from '../../../_shared/helpers/bidPlaceErrorCodeToMessageId';
import useUpdateBidContextWithApi from '../../../_shared/hooks/trade/useUpdateBidContextWithApi';
import updateBidContextWithAuctionSummary from '../../../_shared/helpers/updateBidContextWithAuctionSummary';

const isOmp = isOmpPlatform();

const BidBox = ({
  isLoading,
  isOMP,
  location,
  presetBidAmount,
  tradeContext,
  handleOmpBidding
}) => {
  const bidService = BidService.get();
  const dispatch = useDispatch();
  const track = useAnalytics();

  const {
    bidContext,
    setBidContext,
    bidModalOpen,
    setBidModalOpen,
    tradeDetails
  } = useContext(BidBoxContext);

  const {
    auctionSummary,
    bidList,
    bidStatus,
    currentBidAmount,
    currentProxyAmount,
    id,
    isAuctionEnded,
    isOutBidSameDealer,
    isOutBidSameDGN,
    mediaCounts,
    numberOfBids,
    tradeState,
    variant
  } = bidContext ?? {};
  const [user, configurations, liveBid] = useSelector(
    state => [
      state[ReducerConstants.AUTH]?.user,
      state[ReducerConstants.AUTH]?.system?.configurations,
      state[ReducerConstants.LIVE_BIDS][id]
    ],
    shallowEqual
  );
  const { data: manufacturers } = useManufacturerList();
  const userLocale = renderLocale(user?.auth);
  const localeVariant = userLocale ? userLocale.substring(0, 2) : null;
  const [bidAmount, setBidAmount] = useState('');
  const [mpBidAmount, setMpBidAmount] = useState('');
  const [bidIncrements, setBidIncrements] = useState(null);
  const [minBidAmount, setMinBidAmount] = useState(50);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [mpBidError, setMpBidError] = useState([]);
  const [trackBidFlag, setTrackBidFlag] = useState(false);
  const [trackBidErrorFlag, setTrackBidErrorFlag] = useState(false);
  const [bidType, setBidType] = useState('');
  const [trackHasBid, setTrackHasBid] = useState(false);
  const [trackShowConfirmModal, setTrackShowConfirmModal] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isToggleChecked, setIsToggleChecked] = useState(false);
  const [confirmModalLogic, setConfirmModalLogic] = useState([]);
  const bidSource = window.location.href.includes(ROUTES.BUYING)
    ? BidSource.MINI_VDP_BIDBOX
    : BidSource.VDP_BIDBOX;
  const {
    isMultiOrgAndEnabled,
    ompBiddingDealer,
    validOmpDealerships
  } = useOmpDealerContext();
  const [hasInvalidDealer, setHasInvalidDealer] = useState(false);

  const updateBidContextWithApi = useUpdateBidContextWithApi({
    tradeId: id,
    isOmp,
    setBidContext,
    location,
    handleOmpBidding,
    shouldFetchBidList: true
  });

  /**
   * Updates the bid list
   */
  const getMostRecentBidList = () => {
    // TODO: Get latest bid/trade info to get the most recent top bidder
  };

  const onAuctionEnded = isEnded => {
    setBidContext(context => ({ ...context, isAuctionEnded: isEnded }));
    getMostRecentBidList();
    // TODO: Remove High Bid/Outbid indicator
    // Long term: Update bid list (make another API call) in order
    // to update the High Bidder properly after the auction ended
    // If there's an error, display an error modal (no internet)
  };

  const dealerId = useMemo(
    () =>
      getSelectedDealerId(
        validOmpDealerships,
        ompBiddingDealer?.organizationId
      ),
    [ompBiddingDealer?.organizationId, validOmpDealerships]
  );

  useMpBidTracking({
    bidUsers: bidList?.users,
    bidAmount: mpBidAmount,
    biddingDealerId: dealerId ? parseInt(dealerId, 10) : null,
    bidSource,
    bidStatus,
    bidType,
    confirmationMpTrackers: {
      confirmation: bidContext?.confirmation,
      highBid: bidContext?.highBid,
      outbidDealerGroup: bidContext?.outbidDealerGroup,
      outbidDealership: bidContext?.outbidDealership
    },
    liveBid,
    manufacturers,
    ecsId: ompBiddingDealer?.organizationId,
    organizationName: ompBiddingDealer?.displayName,
    numberOfBids,
    proxyAmount: null,
    setMpBidError,
    setTrackBidErrorFlag,
    setTrackBidFlag,
    setTrackHasBid,
    setTrackProxyFlag: null,
    setTrackShowConfirmModal,
    trackBidErrorFlag,
    trackBidFlag,
    trackHasBid,
    trackProxyFlag: false,
    trackShowConfirmModal,
    tradeDetails: bidContext,
    tradeState,
    userId: user?.id,
    isOmp
  });

  useTradeChange(id, setBidContext);

  useEffect(() => {
    if (auctionSummary?.maxBid) {
      setBidContext(context => ({
        ...context,
        currentBidAmount: auctionSummary?.maxBid
      }));
    }
  }, [setBidContext, auctionSummary?.maxBid]);

  useEffect(() => {
    if (typeof auctionSummary?.numBids === 'number') {
      setBidContext(context => ({
        ...context,
        numberOfBids: auctionSummary?.numBids
      }));
    }
  }, [setBidContext, auctionSummary?.numBids]);

  useEffect(() => {
    setBidAmount(presetBidAmount);
  }, [presetBidAmount]);

  useEffect(() => {
    const increment = getQuickBidIncrements(
      currentBidAmount || auctionSummary?.minBidThreshold,
      configurations
    );

    const startingAmount = currentBidAmount
      ? currentBidAmount + increment
      : auctionSummary?.minBidThreshold;

    setBidIncrements(increment);
    setMinBidAmount(startingAmount);
  }, [currentBidAmount, auctionSummary?.minBidThreshold, configurations]);

  useEffect(() => {
    setBidIncrements(
      getQuickBidIncrements(auctionSummary?.maxBid || 0, configurations)
    );
  }, [auctionSummary?.maxBid, configurations]);

  const setContextOnClick = () => {
    setBidContext(context => ({
      ...context,
      isSettingProxy: true,
      bidSource
    }));
  };

  const onProxyToggleChange = () => {
    if (!bidModalOpen) {
      setContextOnClick();
      setBidModalOpen(true);
    }
  };

  const onProxyButtonClick = () => {
    setContextOnClick();
    setBidModalOpen(true);
  };

  const auctionId = auctionSummary?.id;

  const bidCreate = usePlaceBid({
    auctionId,
    dealerId,
    isOmp,
    tradeId: id
  });

  const submitBidClick = async (
    quickBidAmount,
    validateSameDealershipOrDGN = true
  ) => {
    setFormSubmitting(true);

    const onSuccessfulBid = async data => {
      setFormSubmitting(false);

      // Update Bid Context from /bid/create response
      updateBidContextWithAuctionSummary(setBidContext, {
        ...data?.auctionSummary,
        biddingDealerId: dealerId ? parseInt(dealerId, 10) : null
      });

      setMpBidAmount(quickBidAmount || bidAmount);
      setBidAmount('');

      // Communicates bidding information to vehicle card if on OMP
      if (handleOmpBidding) {
        handleOmpBidding({
          tradeId: id,
          auctionSummary: data?.auctionSummary,
          source: location
        });
      }

      if (
        location === TradeViewVariant.VDP &&
        window.location.href.includes(ROUTES.BUYING)
      ) {
        dispatch(
          updateSelectedTradeAuctionSummary({
            tradeState,
            auctionSummary: bidCreate?.auctionSummary
          })
        );
      }

      TRToast(
        <Alert
          variant="success"
          messages={[<FormattedMessage id="vdp.success.bidPlaced" />]}
        />
      );

      const bidListResponse = await bidService.getBidList(id);
      dispatch(bidSlice.actions.setBidList(bidListResponse));
      setTrackBidFlag(true);
    };

    const onErrorBidding = error => {
      const { errorCode, message } = error;
      setFormSubmitting(false);

      // It is only possible for this statement to be true in OMP
      if (
        [
          BidPlaceErrorCode.OUTBID_DEALERSHIP,
          BidPlaceErrorCode.OUTBID_DGN
        ].includes(errorCode)
      ) {
        const onOmpConfirmModal = async () => {
          submitBidClick(quickBidAmount, false);
          setIsConfirmationOpen(false);
          setConfirmModalLogic([]);
          if (track) {
            track(mixpanelConfig.names.outbidModal, {
              is_modal_confirmed: true
            });
          }
        };

        const onOmpCancelModal = () => {
          setIsConfirmationOpen(false);
          setConfirmModalLogic([]);
          if (track) {
            track(mixpanelConfig.names.outbidModal, {
              is_modal_confirmed: false
            });
          }
        };

        const modalDetails = {
          confirmationTypesList: [
            errorCode === BidPlaceErrorCode.OUTBID_DEALERSHIP
              ? BidConfirmationModalType.outbidDealership
              : BidConfirmationModalType.outbidDGN
          ],
          confirmationModalLogic: setConfirmModalLogic,
          bid: bidAmount,
          confirmModal: onOmpConfirmModal,
          cancelModal: onOmpCancelModal,
          quickBidAmount
        };
        getBidModalLogic(modalDetails);
        setIsConfirmationOpen(true);

        return;
      }

      const errorMessage = stringToLiteral(message);
      if (errorMessage) {
        ErrorService.trackError(error);
        if (errorCode === bidErrors.AUCTION_ENDED) {
          setBidContext(context => ({
            ...context,
            isAuctionEnded: true
          }));
        }
        // When these error codes occur, it's likely the user has outdated information on their screen because they haven't received an SSE event.
        if (FetchAuctionErrorCodes.includes(errorCode)) {
          setBidAmount('');
          track(mixpanelConfig.names.fetchAuctionSummary, {
            source: location,
            trade_id: id,
            attempted_bid_amount: quickBidAmount,
            auction_id: auctionId,
            current_bid_amount: bidContext?.currentBidAmount
          });
          updateBidContextWithApi();
        }
        if (isOmp) {
          // We rely on FE to translate the error message for bid/place API
          const messageId = bidPlaceErrorCodeToMessageId(errorCode);
          TRToast(
            <Alert
              variant="error"
              messages={[<FormattedMessage id={messageId} />]}
            />,
            {
              autoClose: timeouts.ERROR
            }
          );
        } else {
          TRToast(<Alert variant="error" messages={[errorMessage]} />, {
            autoClose: timeouts.ERROR
          });
        }
        mpBidError.push(errorMessage);
        setTrackBidErrorFlag(true);
      } else {
        TRToast(
          <Alert
            variant="error"
            messages={[<FormattedMessage id="vdp.error.placingBid" />]}
          />,
          { autoClose: timeouts.ERROR }
        );
      }
    };

    bidCreate.mutate(
      {
        bidAmount: quickBidAmount || bidAmount,
        // Only validate same dealership & DGN using backend response in OMP, and NOT TradeRev
        validateSameDealershipOrDGN: isOmp && validateSameDealershipOrDGN
      },
      { onSuccess: onSuccessfulBid, onError: onErrorBidding }
    );
  };

  /**
   * TODO: Reuse the Bid Service method for this
   */
  const validateBidAmount = () => {
    const validation = [];

    if (bidAmount) {
      if (currentBidAmount) {
        if (bidAmount <= currentBidAmount) {
          validation.push(<FormattedMessage id="vdp.error.bidTooLow" />);
          mpBidError.push('bid_low');
        }
      } else if (bidAmount < auctionSummary?.minBidThreshold) {
        validation.push(
          <FormattedMessage
            id="vdp.error.startingBidLow"
            values={{
              minBidAmount: convertToCurrency(auctionSummary?.minBidThreshold)
            }}
          />
        );
        mpBidError.push('bid_below_min_threshold');
      } else if (
        bidAmount % getQuickBidIncrements(bidAmount, configurations) !==
        0
      ) {
        validation.push(
          <FormattedMessage
            id="vdp.error.wrongBidIncrement"
            values={{
              quickBidIncrement: convertToCurrency(
                getQuickBidIncrements(bidAmount, configurations)
              )
            }}
          />
        );
        mpBidError.push('wrong_bid_increment');
      }
    }

    return validation;
  };

  const isFirstBid = () => {
    return (
      (tradeState === TradeState.active &&
        (!bidStatus || bidStatus === BidStatus.none)) ||
      (tradeState === TradeState.upcoming && !currentProxyAmount)
    );
  };

  const isBidHigh = () => {
    const highBidThreshold = auctionSummary?.highBidThreshold;
    return (
      bidAmount >= highBidThreshold &&
      (!currentBidAmount || currentBidAmount < highBidThreshold)
    );
  };

  const onConfirmModal = async quickBidAmount => {
    if (isFirstBid()) {
      setTrackShowConfirmModal(true);
      setTrackHasBid(true);
    }
    await submitBidClick(quickBidAmount);

    setFormSubmitting(false);
    setIsConfirmationOpen(false);
    setConfirmModalLogic([]);
  };

  const onCancelModal = () => {
    if (isFirstBid()) {
      setTrackShowConfirmModal(true);
      setTrackHasBid(false);
    }

    setFormSubmitting(false);
    setIsConfirmationOpen(false);
    setConfirmModalLogic([]);
  };

  const getConfirmationTypesList = quickBidAmount => {
    // Initial check for warning modals on open
    const confirmationTypes = [];

    // If user's first bid on trade card
    if (isFirstBid()) {
      setBidContext(context => ({ ...context, confirmation: true }));
      confirmationTypes.push(BidConfirmationModalType.legal);
    }

    // If the bid is very high
    if (!quickBidAmount && isBidHigh()) {
      setBidContext(context => ({ ...context, highBid: true }));
      confirmationTypes.push(BidConfirmationModalType.overBidThreshold);
      mpBidError.push('bid_above_max_threshold');
    }

    if (isOutBidSameDealer) {
      // If there is already another bid from the same dealership
      confirmationTypes.push(BidConfirmationModalType.outbidDealership);
      setBidContext(context => ({
        ...context,
        isOutBidSameDealer: true
      }));
    } else if (isOutBidSameDGN) {
      confirmationTypes.push(BidConfirmationModalType.outbidDGN);
      setBidContext(context => ({ ...context, isOutBidSameDGN: true }));
    }

    return confirmationTypes;
  };

  /**
   * Validation
   */
  const validateForm = quickBidAmount => {
    const bidValidationErrors = validateBidAmount();
    let formValid = !bidValidationErrors.length > 0 || quickBidAmount;

    if (!ompBiddingDealer && isMultiOrgAndEnabled) {
      setHasInvalidDealer(true);
      return false;
    }

    setHasInvalidDealer(false);
    setFormErrors({
      ERROR_BID: quickBidAmount ? false : bidValidationErrors.length > 0
    });

    if (formValid) {
      const modalDetails = {
        confirmationTypesList: getConfirmationTypesList(quickBidAmount),
        confirmationModalLogic: setConfirmModalLogic,
        bid: bidAmount,
        proxy: undefined,
        confirmModal: onConfirmModal,
        cancelModal: onCancelModal,
        quickBidAmount
      };
      getBidModalLogic(modalDetails);
      if (getConfirmationTypesList(quickBidAmount)?.length !== 0)
        formValid = false;

      setIsConfirmationOpen(!formValid);
      // sends bid error event to mixpanel
      setTrackBidErrorFlag(true);
      return formValid;
    }

    if (!formValid) {
      TRToast(<Alert variant="error" messages={[...bidValidationErrors]} />, {
        autoClose: timeouts.ERROR
      });
      // sends bid error event to mixpanel
      setTrackBidErrorFlag(true);
    }

    return formValid;
  };

  /**
   * Bid Button Click
   */
  const handleBidClick = quickBidAmount => {
    if (!bidAmount && !quickBidAmount) {
      setIsToggleChecked(false);
      setBidContext(context => ({
        ...context,
        bidSource
      }));
      setBidModalOpen(true);
      return;
    }

    setBidType(quickBidAmount ? BidType.quickBid : BidType.manual);

    const formValid = validateForm(quickBidAmount);
    if (!formValid) {
      return;
    }

    submitBidClick(quickBidAmount);
  };

  /**
   * Quick Bid
   */
  const onQuickBidButtonClick = bidIncrement => {
    const newAmount = auctionSummary?.maxBid
      ? auctionSummary?.maxBid + bidIncrement
      : auctionSummary?.minBidThreshold;

    setBidAmount(newAmount);
    handleBidClick(newAmount);
  };

  /**
   * Bid Amount
   */
  const onBidAmountChange = useCallback(amount => {
    if (!amount) {
      setBidAmount('');
      return;
    }
    setBidAmount(amount);
  }, []);

  const isSelling = tradeContext === TradeContext.Sell;
  const [biddingDealerCanBid, setBiddingDealerCanBid] = useState(true);

  useEffect(() => {
    if (isOMP && ompBiddingDealer) {
      setBiddingDealerCanBid(
        ompBiddingDealer.paymentAccountSummaries &&
          Object.keys(ompBiddingDealer.paymentAccountSummaries)?.length > 0
      );
    }
  }, [isOMP, ompBiddingDealer, validOmpDealerships]);

  const addPaymentMethodOnClick = useCallback(e => {
    e.preventDefault();
    window.open('/settings/purchase', '_self');
  }, []);

  const biddingDealerId = useMemo(() => {
    if (
      tradeState === TradeState.upcoming ||
      auctionSummary?.userBidStatus !== BidStatus.none
    ) {
      return auctionSummary?.biddingDealerId;
    }
    return null;
  }, [
    auctionSummary?.biddingDealerId,
    auctionSummary?.userBidStatus,
    tradeState
  ]);

  return (
    <StyledBidBox
      variant={variant}
      localeVariant={localeVariant}
      tradeState={tradeState}
      isSelling={isSelling}
      data-testid="vdp-bid-box"
      isMultiOrg={isMultiOrgAndEnabled}
    >
      {isOmp && <StyledFadeBorder />}
      <Header
        isLoading={isLoading}
        onAuctionEnded={onAuctionEnded}
        isOmp={isOMP}
      />
      <StyledContent tradeState={tradeState} id="styledContent">
        <Summary
          isLoading={isLoading}
          variant={
            tradeState === TradeState.pending
              ? SUMMARY_VARIANT.PENDING
              : SUMMARY_VARIANT.OVERVIEW
          }
          onAuctionEnded={onAuctionEnded}
          bidContext={bidContext}
          isSelling={isSelling}
          manufacturers={manufacturers}
          isAuctionExtended={
            isLoading && tradeDetails?.auctionSummary?.isTimerExtended
          }
          testId="vdp-bid-box-summary"
          isOMP={isOMP}
        />
        {tradeState !== TradeState.pending && (
          <History
            isLoading={isLoading}
            variant="overview"
            showHistoryPreview={!isSelling}
            bidContext={bidContext}
            floorPrice={tradeDetails?.tradeFloorPrice?.valueDollars}
            testId="vdp-bid-box-history"
          />
        )}
      </StyledContent>
      <StyledBiddingSection
        variant={variant}
        isAuctionEnded={isAuctionEnded}
        tradeState={tradeState}
        isMultiOrg={isMultiOrgAndEnabled}
      >
        {tradeState !== TradeState.pending && isOMP && (
          <>
            {isMultiOrgAndEnabled && (
              <DealerSelectDropdown
                biddingDealerId={biddingDealerId}
                hasError={hasInvalidDealer}
                isLoading={isLoading}
              />
            )}
            {!biddingDealerCanBid && (
              <div style={{ display: 'block', width: '100%' }}>
                <StyledKarStatusBanner status={FeedbackStatus.Error}>
                  <DealerNoPaymentMethodContainer>
                    <FormattedMessage id="vdp.bid.no.payment.method.message" />
                    <StyledAddPaymentMethodLink
                      onClick={addPaymentMethodOnClick}
                    >
                      <FormattedMessage id="vdp.bid.add.payment.method" />
                    </StyledAddPaymentMethodLink>
                  </DealerNoPaymentMethodContainer>
                </StyledKarStatusBanner>
              </div>
            )}
          </>
        )}
        <BidControlsContainer>
          {![TradeState.upcoming, TradeState.pending].includes(tradeState) &&
            !currentProxyAmount &&
            !isLoading &&
            !isSelling &&
            (!isOMP || (isOMP && biddingDealerCanBid)) && (
              <BidFormActive
                onProxyToggleChange={() => {
                  setBidAmount('');
                  onProxyToggleChange();
                }}
                bidIncrements={bidIncrements}
                bidAmount={bidAmount}
                minBidAmount={minBidAmount}
                isToggleChecked={isToggleChecked}
                formErrors={formErrors}
                onBidAmountChange={onBidAmountChange}
              />
            )}
          {(!isOMP ||
            (isOMP &&
              (biddingDealerCanBid || tradeState === TradeState.pending))) && (
            <Controls
              bidAmount={bidAmount}
              bidIncrements={bidIncrements}
              bidStatus={bidStatus}
              currentProxyAmount={currentProxyAmount}
              currentBidAmount={currentBidAmount}
              formSubmitting={formSubmitting}
              isAuctionEnded={isAuctionEnded}
              isLoading={isLoading}
              isSelling={isSelling}
              mediaCounts={mediaCounts}
              numberOfBids={numberOfBids}
              onBidButtonClick={handleBidClick}
              onQuickBidButtonClick={onQuickBidButtonClick}
              onProxyButtonClick={() => {
                setBidAmount('');
                setIsToggleChecked(false);
                onProxyButtonClick();
              }}
              onProxyToggleChange={onProxyToggleChange}
              tradeState={tradeState}
              variant={TradeViewVariant.VDP}
              tradeId={id}
              minBidAmount={auctionSummary?.minBidThreshold}
              isOmp={isOMP}
            />
          )}
        </BidControlsContainer>
        {tradeState === TradeState.closing && !tradeDetails.isSelling && (
          <StyledBiddingDisclaimer>
            <FormattedMessage id="vdp.closing.buyer.disclaimer" />
          </StyledBiddingDisclaimer>
        )}
      </StyledBiddingSection>
      <ConfirmModal
        location={TradeViewVariant.VDP}
        isOpen={isConfirmationOpen}
        modalLogic={confirmModalLogic}
        isLoading={formSubmitting}
        loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
      />
    </StyledBidBox>
  );
};

export default BidBox;
